'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/local-api/client';

export interface AppUserProfile {
  id: string;
  full_name: string;
  email?: string;
  username?: string;
  bio?: string;
  avatar_url?: string;
  cover_url?: string;
  role?: string;
  posts_count?: number;
  followers_count?: number;
  following_count?: number;
  created_at?: string;
}

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<AppUserProfile | null>(null);
  const supabase = createClient();

  const fetchProfile = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('user_profiles')
      .select('id, full_name, email, username, bio, avatar_url, cover_url, role, posts_count, followers_count, following_count, created_at')
      .eq('id', userId)
      .single();
    if (data) setProfile(data as AppUserProfile);
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Real-time profile sync — listens for changes to the current user's profile row
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`profile_sync_${user.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'user_profiles', filter: `id=eq.${user.id}` },
        (payload) => {
          setProfile((prev) => prev ? { ...prev, ...(payload.new as AppUserProfile) } : (payload.new as AppUserProfile));
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    await fetchProfile(user.id);
  }, [user, fetchProfile]);

  // Email or mobile number and password sign up.
  const signUp = async (identifier: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      identifier,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          inviter_id: metadata?.inviterId || undefined,
          avatar_url: metadata?.avatarUrl || ''
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email or mobile number and password sign in.
  const signIn = async (identifier: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      identifier,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Update User Profile — also updates local profile state immediately
  const updateUserProfile = async (updates: {
    full_name?: string;
    username?: string;
    bio?: string;
    avatar_url?: string;
    cover_url?: string;
  }) => {
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    // Immediately update local profile state so all consumers re-render
    setProfile((prev) => prev ? { ...prev, ...updates } : (data as AppUserProfile));
    return data;
  };

  const value = {
    user,
    session,
    loading,
    profile,
    refreshProfile,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    updateUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
